/**
 * Events API hooks — TanStack Query wrappers for the /api/events endpoints.
 *
 * Scoping model:
 *   scope=account   → all events the user can see (read-only)
 *   scope=workspace → events in a given workspace  (read-only)
 *   scope=film      → events in a given film        (full CRUD)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../config/api';

/* ── Key factories ────────────────────────────────────────────────── */
export const eventKeys = {
  all: ['events'],
  account: (filters) => ['events', 'account', filters],
  workspace: (wsId, filters) => ['events', 'workspace', wsId, filters],
  film: (filmId, filters) => ['events', 'film', filmId, filters],
  detail: (id) => ['events', 'detail', id],
};

/* ── Fetch helpers ────────────────────────────────────────────────── */
async function fetchEvents({ scope, workspace_id, film_id, start_at, end_at, event_type }) {
  const params = { scope };
  if (workspace_id) params.workspace_id = workspace_id;
  if (film_id) params.film_id = film_id;
  if (start_at) params.start_at = start_at;
  if (end_at) params.end_at = end_at;
  if (event_type && event_type.length > 0) {
    // FastAPI expects repeated query params for List[str]
    params.event_type = event_type;
  }
  const { data } = await apiClient.get('/events', { params, paramsSerializer: { indexes: null } });
  return data;
}

async function fetchEvent(id) {
  const { data } = await apiClient.get(`/events/${id}`);
  return data;
}

async function createEvent(body) {
  const { data } = await apiClient.post('/events', body);
  return data;
}

async function updateEvent({ id, ...body }) {
  const { data } = await apiClient.patch(`/events/${id}`, body);
  return data;
}

async function deleteEvent(id) {
  await apiClient.delete(`/events/${id}`);
}

/* ── Query hooks ──────────────────────────────────────────────────── */

/** List events for the given scope + filters. */
export function useEvents({ scope = 'account', workspace_id, film_id, start_at, end_at, event_type } = {}) {
  const filters = { start_at, end_at, event_type };
  const key =
    scope === 'film'
      ? eventKeys.film(film_id, filters)
      : scope === 'workspace'
        ? eventKeys.workspace(workspace_id, filters)
        : eventKeys.account(filters);

  return useQuery({
    queryKey: key,
    queryFn: () => fetchEvents({ scope, workspace_id, film_id, start_at, end_at, event_type }),
    enabled:
      scope === 'account' ||
      (scope === 'workspace' && !!workspace_id) ||
      (scope === 'film' && !!film_id),
  });
}

/** Single event by id. */
export function useEvent(id) {
  return useQuery({
    queryKey: eventKeys.detail(id),
    queryFn: () => fetchEvent(id),
    enabled: !!id,
  });
}

/* ── Mutation hooks ───────────────────────────────────────────────── */

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createEvent,
    onSuccess: () => qc.invalidateQueries({ queryKey: eventKeys.all }),
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateEvent,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: eventKeys.all });
      qc.setQueryData(eventKeys.detail(data.id), data);
    },
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteEvent,
    onSuccess: () => qc.invalidateQueries({ queryKey: eventKeys.all }),
  });
}
